import React, { useRef } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import clsx from 'clsx';
import { ChipsGroupTransitionProvider } from './chip-group-transition-provider';
import { Chip } from '../chip';
import styles from './styles.module.css';
/**
 * This component is a transition orchestrator of multiple `<Chip/>` component
 *
 * It must be a direct parent of `<Chip/>` components or their wrappers.
 *
 * @example correct
 * ```jsx
 * <div className="list">
 *      <ChipGroup>
 *          <div className="listitem">
 *              <Chip />
 *          </div>
 *      </ChipGroup>
 * </div>
 * ```
 */

export const ChipsGroup = ({
  children,
  className,
  ...rest
}) => {
  const nodeRef = useRef(null);
  return /*#__PURE__*/React.createElement(TransitionGroup, {
    className: clsx(styles.chipsGroup, className),
    childFactory: child => {
      const {
        key,
        props,
        type
      } = child; // eslint-disable-next-line @typescript-eslint/naming-convention

      const {
        in: _in,
        onExited,
        enter,
        exit,
        appear,
        children: nestedChildren,
        ...actualProps
      } = props;
      const transitionProps = {
        in: _in,
        onExited,
        enter,
        exit,
        appear
      };
      const childContainsChip = findChip(nestedChildren);
      const nestedElement = /*#__PURE__*/React.createElement(type, { ...actualProps,
        ref: nodeRef
      }, nestedChildren);
      return childContainsChip ? /*#__PURE__*/React.createElement(ChipsGroupTransitionProvider, {
        key: key,
        ...transitionProps
      }, nestedElement) : /*#__PURE__*/React.createElement(CSSTransition, {
        nodeRef: nodeRef,
        key: key,
        timeout: 0,
        ...transitionProps
      }, nestedElement);
    },
    ...rest
  }, children);
};
export const findChip = children => {
  const recursiveFindChip = child => {
    var _child$props;

    if ((child == null ? void 0 : child.type) === Chip) {
      return true;
    }

    if (child != null && (_child$props = child.props) != null && _child$props.children) {
      return recursiveFindChip(child.props.children);
    }

    return false;
  };

  return recursiveFindChip(children);
};